import { ReactNode } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../modules/localization/translations"
import { MeetingDetailsOrder } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import {
    STATUS_CHIP_VARIANT,
    STATUS_MAP,
} from "../../../modules/merchant/merchant/production/meetingService/constants"
import { StandardChip } from "../badges/StandardChip"
import { ExpandableText } from "../text/ExpandableText"

const useStyles = makeStyles()((theme) => ({
    detailsCard: {
        padding: "0 1rem",
        borderRadius: ".75rem",
        overflow: "hidden",
        background: theme.palette.background.paper,
    },
    detailRow: {
        padding: ".75rem 0",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
        "&:last-child": { borderBottom: "none" },
    },
    detailLabel: {
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.tailwind.slate[500],
    },
    detailValue: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.tailwind.slate[800],
        textAlign: "right",
    },
    noteCallout: {
        marginTop: "1rem",
        padding: ".75rem 1rem",
        borderRadius: ".75rem",
        background: theme.palette.tailwind.amber[50],
    },
    noteTitle: {
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.tailwind.yellow[700],
        textTransform: "uppercase",
    },
    noteText: {
        color: theme.palette.tailwind.stone[700],
    },
}))

type Props = Readonly<{
    details: MeetingDetailsOrder
}>

export function OrderDetailsCard({ details }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const sittings = details.deliveryDetails.sittings ?? 0
    const contactName = details.customer?.displayName ?? ""
    const deliveryType = details.deliveryDetails.type
    const deliveryTypeLabel = deliveryType
        ? l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType)
        : ""
    const locationName = details.deliveryDetails.location?.name ?? ""
    const orderStatus = details.deliveryDetails.statusDetails?.status
    const deliveryDateTime = dateTimeFactory.fromApi(details.deliveryDetails.time ?? "")
    const deliveryTimeLabel = deliveryDateTime.isValid
        ? `${dateTimeFactory.formatDayAndDate(deliveryDateTime)} · ${dateTimeFactory.formatTime(deliveryDateTime)}`
        : "-"

    const detailRows: ReadonlyArray<{
        key: string
        labelId: TranslationKey
        labelFallback: string
        value: ReactNode
        isElement?: boolean
    }> = [
        ...(orderStatus
            ? [
                  {
                      key: "status",
                      labelId: "meeting-service-order-drawer-status" as TranslationKey,
                      labelFallback: "Status",
                      value: (
                          <LocalizedStrict id={STATUS_MAP[orderStatus].stateTranslationId}>
                              <StandardChip variant={STATUS_CHIP_VARIANT[orderStatus]}>
                                  {STATUS_MAP[orderStatus].statusItemLabel}
                              </StandardChip>
                          </LocalizedStrict>
                      ),
                      isElement: true,
                  },
              ]
            : []),
        {
            key: "time",
            labelId: "meeting-service-order-drawer-delivery-time",
            labelFallback: "Delivery time",
            value: deliveryTimeLabel,
        },
        {
            key: "contact",
            labelId: "meeting-service-order-drawer-contact",
            labelFallback: "Contact",
            value: contactName,
        },
        {
            key: "covers",
            labelId: "meeting-service-order-drawer-covers",
            labelFallback: "Covers",
            value: (
                <LocalizedStrict id="meeting-service-order-drawer-covers-value" vars={{ count: sittings }}>
                    <span>{`${sittings} people`}</span>
                </LocalizedStrict>
            ),
        },
        {
            key: "type",
            labelId: "meeting-service-order-drawer-delivery-type",
            labelFallback: "Delivery type",
            value: deliveryTypeLabel,
        },
        ...(locationName
            ? [
                  {
                      key: "location",
                      labelId: "meeting-service-order-drawer-location" as TranslationKey,
                      labelFallback: "Location",
                      value: locationName,
                  },
              ]
            : []),
    ]

    return (
        <>
            <Box className={classes.detailsCard}>
                {detailRows.map((row) => (
                    <Stack
                        key={row.key}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing="1rem"
                        className={classes.detailRow}
                    >
                        <LocalizedStrict id={row.labelId}>
                            <Typography
                                component="span"
                                variant="body2"
                                className={classes.detailLabel}
                            >
                                {row.labelFallback}
                            </Typography>
                        </LocalizedStrict>
                        {row.isElement ? (
                            row.value
                        ) : (
                            <Typography
                                component="span"
                                variant="body2"
                                className={classes.detailValue}
                            >
                                {row.value}
                            </Typography>
                        )}
                    </Stack>
                ))}
            </Box>

            {!!details.note && (
                <Box className={classes.noteCallout}>
                    <Stack direction="row" alignItems="center" spacing=".5rem" marginBottom=".25rem">
                        <FontAwesomeIcon icon={faComment} size="sm" className={classes.noteTitle} />
                        <LocalizedStrict id="meeting-service-order-drawer-order-note">
                            <Typography
                                component="span"
                                variant="caption"
                                className={classes.noteTitle}
                            >
                                Order note
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                    <ExpandableText text={details.note} className={classes.noteText} />
                </Box>
            )}
        </>
    )
}
